import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiAlertCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Button } from '../../ui/Button';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { API_URL } from '../../../services/api';

interface ServiceType {
  id: string;
  nameTh: string;
  nameEn?: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
}

interface ServiceTypeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: ServiceType | null;
  onSuccess: () => void;
}

interface FormData {
  nameTh: string;
  nameEn: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
}

const ServiceTypeFormModal: React.FC<ServiceTypeFormModalProps> = ({
  isOpen,
  onClose,
  serviceType,
  onSuccess,
}) => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nameTh: '',
    nameEn: '',
    description: '',
    displayOrder: 1,
    isActive: true,
  });

  // Reset form when modal opens or serviceType changes
  useEffect(() => {
    if (isOpen) {
      if (serviceType) {
        // Edit mode
        setFormData({
          nameTh: serviceType.nameTh,
          nameEn: serviceType.nameEn || '',
          description: serviceType.description || '',
          displayOrder: serviceType.displayOrder,
          isActive: serviceType.isActive,
        });
      } else {
        // Add mode
        setFormData({
          nameTh: '',
          nameEn: '',
          description: '',
          displayOrder: 1,
          isActive: true,
        });
      }
      setError(null);
    }
  }, [isOpen, serviceType]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'displayOrder') {
      setFormData((prev) => ({ ...prev, [name]: parseInt(value) || 1 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.nameTh.trim()) {
      setError('กรุณากรอกชื่อประเภทบริการ (ภาษาไทย)');
      return;
    }

    if (formData.displayOrder < 1) {
      setError('ลำดับการแสดงผลต้องมากกว่า 0');
      return;
    }

    try {
      setLoading(true);

      
      const endpoint = serviceType
        ? `/api/admin/service-types/${serviceType.id}`
        : '/api/admin/service-types';
      const method = serviceType ? 'PUT' : 'POST';

      // Prepare data - remove empty strings (use camelCase)
      const submitData = {
        nameTh: formData.nameTh.trim(),
        nameEn: formData.nameEn.trim() || null,
        description: formData.description.trim() || null,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || result.message || 'เกิดข้อผิดพลาด');
      }

      // Success
      toast.success(serviceType ? 'แก้ไขประเภทบริการเรียบร้อยแล้ว' : 'เพิ่มประเภทบริการเรียบร้อยแล้ว');
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-white rounded-lg shadow-xl max-w-lg w-full"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {serviceType ? 'แก้ไขประเภทบริการ' : 'เพิ่มประเภทบริการใหม่'}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={loading}
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-4">
                  {/* Error Message */}
                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
                      <FiAlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium">เกิดข้อผิดพลาด</p>
                        <p className="text-sm">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Name Thai */}
                  <div className="mb-4">
                    <label htmlFor="nameTh" className="block text-sm font-medium text-gray-700 mb-1">
                      ชื่อประเภทบริการ (ภาษาไทย) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nameTh"
                      name="nameTh"
                      value={formData.nameTh}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="เช่น รักษาความปลอดภัย"
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Name English */}
                  <div className="mb-4">
                    <label htmlFor="nameEn" className="block text-sm font-medium text-gray-700 mb-1">
                      ชื่อประเภทบริการ (ภาษาอังกฤษ)
                    </label>
                    <input
                      type="text"
                      id="nameEn"
                      name="nameEn"
                      value={formData.nameEn}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g. Security Guard"
                      disabled={loading}
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      คำอธิบาย
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="คำอธิบายเกี่ยวกับประเภทบริการนี้"
                      disabled={loading}
                    />
                  </div>

                  {/* Display Order */}
                  <div className="mb-4">
                    <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 mb-1">
                      ลำดับการแสดงผล <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="displayOrder"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleChange}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      disabled={loading}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      ตัวเลขน้อยจะแสดงก่อน
                    </p>
                  </div>

                  {/* Is Active */}
                  <div className="mb-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={loading}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        เปิดใช้งาน
                      </span>
                    </label>
                    <p className="mt-1 ml-6 text-xs text-gray-500">
                      เมื่อปิดใช้งาน ประเภทบริการนี้จะไม่แสดงในระบบ
                    </p>
                  </div>

                  {/* Footer Buttons */}
                  <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={loading}
                    >
                      ยกเลิก
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      leftIcon={<FiSave />}
                      loading={loading}
                      loadingText="กำลังบันทึก..."
                    >
                      {serviceType ? 'บันทึกการแก้ไข' : 'เพิ่มประเภทบริการ'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ServiceTypeFormModal;
